import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useTranslatedText from 'src/hooks/useTranslatedText';
import { GET_MEDIATOR_PORTAL_URL } from 'src/graphQL/queries';
import { MEDIATOR_PORTAL_URL, MEDIATION_SUPPORT_EMAIL } from 'src/config';
import VendorPage from 'src/sections/vendorPortal/VendorPage';
import { InlineError, Section, metaSx } from 'src/components/universe/Surface';
import Icon from 'src/components/universe/Icon';
import {
  primaryButtonSx,
  secondaryButtonSx
} from 'src/theme/universe/buttonSx';

export default function MediatorPortalRedirect({ allocationId: fromProps }) {
  const { t } = useTranslatedText();
  const theme = useTheme();
  const params = useParams();
  const allocationId = fromProps || params.allocationId || params.id;
  const { data, loading, error, refetch } = useQuery(GET_MEDIATOR_PORTAL_URL, {
    fetchPolicy: 'cache-first'
  });
  const candidate = (
    MEDIATOR_PORTAL_URL ||
    data?.mediatorPortalUrl ||
    ''
  ).replace(/\/+$/, '');
  let base = '';
  try {
    const parsed = new URL(candidate);
    if (['http:', 'https:'].includes(parsed.protocol)) base = candidate;
  } catch (error) {
    /* An unavailable address uses the support route below. */
  }
  const url = base
    ? base +
      (allocationId
        ? '/incarichi/' + encodeURIComponent(allocationId)
        : '/incarichi')
    : '';
  useEffect(() => {
    if (url) window.location.replace(url);
  }, [url]);
  const subject = allocationId
    ? 'Accesso al Portale Mediatori — incarico ' + allocationId
    : 'Accesso al Portale Mediatori';
  const body = [
    'Buongiorno,',
    'non riesco ad aprire il mio incarico dal link ricevuto per email.',
    allocationId ? 'Codice incarico: ' + allocationId : null,
    'Grazie.'
  ]
    .filter(Boolean)
    .join('\n');
  const support =
    'mailto:' +
    MEDIATION_SUPPORT_EMAIL +
    '?subject=' +
    encodeURIComponent(subject) +
    '&body=' +
    encodeURIComponent(body);
  return (
    <VendorPage document={t('Portale Mediatori')} width="prose">
      <Section
        title={t(
          'Gli incarichi di mediazione si gestiscono sul Portale Mediatori'
        )}
      >
        <Stack spacing={2}>
          <Typography sx={metaSx}>
            {t(
              "Proposte, accettazione, dettagli dell'incarico e inizio/fine intervento sono tutti nel Portale Mediatori: non serve più passare da LingoYou Universe."
            )}
          </Typography>
          {loading && !url && (
            <>
              <Skeleton height={24} />
              <Typography role="status" sx={metaSx}>
                {t('Ti stiamo portando sul Portale Mediatori…')}
              </Typography>
            </>
          )}
          {url && (
            <>
              <Typography role="status" sx={metaSx}>
                {t(
                  'Ti stiamo portando sul Portale Mediatori… Se non succede nulla, usa il pulsante qui sotto.'
                )}
              </Typography>
              <Button
                href={url}
                startIcon={<Icon name="ExportSquare" size="sm" />}
                sx={primaryButtonSx(theme)}
              >
                {t('Apri il Portale Mediatori')}
              </Button>
            </>
          )}
          {!loading && !url && (
            <>
              <Typography sx={metaSx}>
                {t(
                  "Non riusciamo ad aprire il Portale Mediatori da questo link. Scrivici indicando il codice qui sotto e ti mandiamo l'accesso al tuo incarico."
                )}
              </Typography>
              {allocationId && (
                <Typography sx={{ fontSize: 14, overflowWrap: 'anywhere' }}>
                  {t('Incarico {{id}}', { id: allocationId })}
                </Typography>
              )}
              {error && (
                <InlineError
                  message={t("L'indirizzo del portale non è disponibile.")}
                  onRetry={() => refetch()}
                />
              )}
              <Button href={support} sx={secondaryButtonSx(theme)}>
                {t('Scrivi a LingoYou')}
              </Button>
            </>
          )}
        </Stack>
      </Section>
    </VendorPage>
  );
}

MediatorPortalRedirect.propTypes = { allocationId: PropTypes.string };
